import React, { useState, useEffect, useRef } from "react";
import "./HROnboarding.css";
import logoImage from "../images/logos/cyberaries-logo.png";

const initialForm = {
    recordType: "Intern",

    fullName: "",
    preferredName: "",
    personalEmail: "",
    phoneNumber: "",
    dateOfBirth: "",
    gender: "",
    currentResidentialAddress: "",
    permanentAddress: "",
    city: "",
    state: "",
    pinCode: "",

    emergencyContactName: "",
    emergencyContactRelationship: "",
    emergencyContactPhone: "",

    highestQualification: "",
    collegeUniversity: "",
    degreeCourse: "",
    branchSpecialization: "",
    currentYearSemester: "",
    graduationYear: "",
    cgpaPercentage: "",
    professionalCertifications: "",

    previousExperience: "No",
    previousOrganization: "",
    previousDesignation: "",
    previousStartDate: "",
    previousEndDate: "",
    previousDuration: "",
    keyResponsibilities: "",

    bankAccountHolderName: "",
    bankName: "",
    accountNumber: "",
    ifsc: "",
    pan: "",
    uanPfDetails: "",
    esicDetails: "",

    declarationAccuracy: false,
    hrProcessingConsent: false,
    documentAuthenticity: false,
    policyAcknowledgement: false,
    confirmation: false,
    codeOfConductAccepted: false,
};

const documentFields = [
    ["resume", "Resume / CV", true],
    ["profilePhoto", "Profile Photograph", true],
    ["panCard", "PAN Card", true],
    ["identityProof", "Identity Proof", true],
    ["addressProof", "Address Proof", true],
    ["educationalCertificates", "Educational Certificates / Marksheets", true],
    ["previousEmploymentDocuments", "Previous Employment / Experience Documents (if applicable)", false],
    ["bankProof", "Bank Proof / Cancelled Cheque", true],
    ["otherDocuments", "Other Onboarding Documents (if applicable)", false],
];

const compressImageFile = async (file) => {
    if (!file || !file.type || !file.type.startsWith("image/") || file.size <= 600 * 1024) {
        return file;
    }
    return new Promise((resolve) => {
        try {
            const img = new Image();
            const url = URL.createObjectURL(file);
            img.onload = () => {
                try {
                    URL.revokeObjectURL(url);
                    const maxDim = 1600;
                    let { width, height } = img;
                    if (width > maxDim || height > maxDim) {
                        if (width > height) {
                            height = Math.round((height * maxDim) / width);
                            width = maxDim;
                        } else {
                            width = Math.round((width * maxDim) / height);
                            height = maxDim;
                        }
                    }
                    const canvas = document.createElement("canvas");
                    canvas.width = width;
                    canvas.height = height;
                    const ctx = canvas.getContext("2d");
                    ctx.drawImage(img, 0, 0, width, height);
                    canvas.toBlob(
                        (blob) => {
                            if (blob && blob.size < file.size) {
                                const compressedFile = new File([blob], file.name, {
                                    type: "image/jpeg",
                                    lastModified: Date.now()
                                });
                                resolve(compressedFile);
                            } else {
                                resolve(file);
                            }
                        },
                        "image/jpeg",
                        0.85
                    );
                } catch {
                    resolve(file);
                }
            };
            img.onerror = () => {
                URL.revokeObjectURL(url);
                resolve(file);
            };
            img.src = url;
        } catch {
            resolve(file);
        }
    });
};

function HROnboarding() {
    const [form, setForm] = useState(initialForm);
    const [files, setFiles] = useState({});
    const [submitted, setSubmitted] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState("");

    const [previewFile, setPreviewFile] = useState(null); // { url, type, name }
    const objectUrlsRef = useRef([]);

    const createLocalUrl = (file) => {
        const url = URL.createObjectURL(file);
        objectUrlsRef.current.push(url);
        return url;
    };

    const cleanLocalUrls = () => {
        objectUrlsRef.current.forEach((url) => URL.revokeObjectURL(url));
        objectUrlsRef.current = [];
    };

    useEffect(() => {
        let meta = document.querySelector('meta[name="robots"]');
        const originalContent = meta ? meta.getAttribute("content") : null;
        if (meta) {
            meta.setAttribute("content", "noindex, nofollow");
        } else {
            meta = document.createElement("meta");
            meta.name = "robots";
            meta.content = "noindex, nofollow";
            document.head.appendChild(meta);
        }
        return () => {
            cleanLocalUrls();
            if (meta) {
                if (originalContent) {
                    meta.setAttribute("content", originalContent);
                } else {
                    meta.remove();
                }
            }
        };
    }, []);

    const handleViewFile = (file) => {
        const url = createLocalUrl(file);
        setPreviewFile({
            url,
            type: file.type.startsWith("image/")
                ? "image"
                : file.type === "application/pdf"
                ? "pdf"
                : "other",
            name: file.name,
        });
    };

    const handleClosePreview = () => {
        setPreviewFile(null);
    };

    const handleRemoveFile = (fieldName, fileIndex) => {
        setFiles((prev) => {
            const currentFiles = prev[fieldName] || [];
            const updated = currentFiles.filter((_, idx) => idx !== fileIndex);
            return {
                ...prev,
                [fieldName]: updated,
            };
        });
    };

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;

        let processedValue = value;

        // Names: Alphabetic, spaces, dots, hyphens only (no numbers or special characters)
        if (
            name === "fullName" ||
            name === "preferredName" ||
            name === "emergencyContactName" ||
            name === "bankAccountHolderName"
        ) {
            processedValue = value.replace(/[^a-zA-Z\s.\-']/g, "");
        }

        // City and State: Letters, spaces, dots, hyphens only
        if (name === "city" || name === "state") {
            processedValue = value.replace(/[^a-zA-Z\s.\-']/g, "");
        }

        // Phone Numbers: Numbers only, max 10 digits
        if (name === "phoneNumber" || name === "emergencyContactPhone") {
            processedValue = value.replace(/\D/g, "").slice(0, 10);
        }

        // PIN Code: Numbers only, max 6 digits
        if (name === "pinCode") {
            processedValue = value.replace(/\D/g, "").slice(0, 6);
        }

        // Graduation Year: Numbers only, max 4 digits
        if (name === "graduationYear") {
            processedValue = value.replace(/\D/g, "").slice(0, 4);
        }

        // Bank Account Number: Numbers only, max 18 digits
        if (name === "accountNumber") {
            processedValue = value.replace(/\D/g, "").slice(0, 18);
        }

        // PAN Card Number: Uppercase alphanumeric, max 10 characters
        if (name === "pan") {
            processedValue = value.toUpperCase().replace(/[^A-Z0-9]/g, "").slice(0, 10);
        }

        // Bank IFSC Code: Uppercase alphanumeric, max 11 characters
        if (name === "ifsc") {
            processedValue = value.toUpperCase().replace(/[^A-Z0-9]/g, "").slice(0, 11);
        }

        // UAN and ESIC: Alphanumeric, max 25 characters
        if (name === "uanPfDetails" || name === "esicDetails") {
            processedValue = value.replace(/[^a-zA-Z0-9]/g, "").slice(0, 25);
        }

        setForm((prev) => ({
            ...prev,
            [name]: type === "checkbox" ? checked : processedValue,
        }));
    };

    const handleFileChange = (e, fieldName) => {
        const selectedFiles = Array.from(e.target.files || []);

        setFiles((prev) => ({
            ...prev,
            [fieldName]: selectedFiles,
        }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        setError("");
        setSubmitting(true);

        // Validation Checks
        if (form.phoneNumber && form.phoneNumber.length !== 10) {
            setError("Personal Phone Number must be exactly 10 digits.");
            setSubmitting(false);
            return;
        }

        if (form.emergencyContactPhone && form.emergencyContactPhone.length !== 10) {
            setError("Emergency Contact Phone must be exactly 10 digits.");
            setSubmitting(false);
            return;
        }

        if (form.pinCode && form.pinCode.length !== 6) {
            setError("PIN Code must be exactly 6 digits.");
            setSubmitting(false);
            return;
        }

        if (form.graduationYear && (form.graduationYear.length !== 4 || Number(form.graduationYear) < 1970 || Number(form.graduationYear) > 2035)) {
            setError("Please enter a valid 4-digit Graduation Year (e.g. 2024).");
            setSubmitting(false);
            return;
        }

        const panRegex = /^[A-Z]{5}[0-9]{4}[A-Z]{1}$/;
        if (form.pan && !panRegex.test(form.pan)) {
            setError("Invalid PAN Card format. Standard format is 5 letters, 4 digits, 1 letter (e.g. ABCDE1234F).");
            setSubmitting(false);
            return;
        }

        if (form.accountNumber && (form.accountNumber.length < 9 || form.accountNumber.length > 18)) {
            setError("Bank Account Number must be between 9 and 18 digits.");
            setSubmitting(false);
            return;
        }

        const ifscRegex = /^[A-Z]{4}0[A-Z0-9]{6}$/;
        if (form.ifsc && !ifscRegex.test(form.ifsc)) {
            setError("Invalid IFSC Code format. IFSC must be 11 characters starting with 4 letters and '0' (e.g. HDFC0001234).");
            setSubmitting(false);
            return;
        }

        if (!form.codeOfConductAccepted) {
            setError("Please read and accept the Code of Conduct before submitting the form.");
            setSubmitting(false);
            return;
        }

        try {
            const formData = new FormData();

            // Add all text/form fields
            Object.entries(form).forEach(([key, value]) => {
                formData.append(
                    key,
                    value === null || value === undefined
                        ? ""
                        : String(value)
                );
            });

            // Add uploaded documents (compressing high-res mobile photos if necessary)
            for (const [fieldName, selectedFiles] of Object.entries(files)) {
                if (!Array.isArray(selectedFiles)) continue;
                for (const file of selectedFiles) {
                    if (file instanceof File) {
                        const processedFile = await compressImageFile(file);
                        formData.append(
                            fieldName,
                            processedFile,
                            processedFile.name
                        );
                    }
                }
            }

            console.log("Submitting HR form...");

            const isLocalhost =
                typeof window !== "undefined" &&
                (window.location.hostname === "localhost" ||
                 window.location.hostname === "127.0.0.1");

            const apiBaseUrl =
                process.env.REACT_APP_API_URL !== undefined
                    ? process.env.REACT_APP_API_URL
                    : (isLocalhost && window.location.port === "3000"
                        ? "http://localhost:5000"
                        : "");

            const response = await fetch(
                `${apiBaseUrl}/api/hr/submit`,
                {
                    method: "POST",
                    body: formData
                }
            );

            console.log(
                "Backend response status:",
                response.status
            );

            if (response.status === 413) {
                throw new Error(
                    "Uploaded documents exceed the maximum allowed size (4.5 MB). Please compress your PDF files or upload smaller files."
                );
            }

            if (response.status === 504) {
                throw new Error(
                    "Upload timed out. Please check your internet connection and try with smaller files."
                );
            }

            const responseText = await response.text();

            console.log(
                "Backend response:",
                responseText
            );

            let result;

            try {
                result = JSON.parse(responseText);
            } catch {
                if (responseText && responseText.trim().startsWith("<")) {
                    if (isLocalhost) {
                        throw new Error(
                            "Backend server is not reachable on port 5000. Please start the backend with: npm run server (or node server/server.js) in a separate terminal."
                        );
                    } else {
                        throw new Error(
                            "Backend API is not yet reachable. Please ensure the latest code is deployed to Vercel."
                        );
                    }
                }
                throw new Error(
                    "Backend returned an invalid response."
                );
            }

            if (!response.ok || !result.success) {
                throw new Error(
                    result.error ||
                    "Unable to submit the form."
                );
            }

            console.log(
                "HR submission successful:",
                result
            );

            setSubmitted(true);

        } catch (err) {

            console.error(
                "HR submission error:",
                err
            );

            setError(
                err.message ||
                "Unable to submit the form. Please try again."
            );

        } finally {

            setSubmitting(false);

        }
    };

    if (submitted) {
        return (
            <main className="hr-page">
                <section className="hr-success">
                    <img
                        src={logoImage}
                        alt="CyberAries Logo"
                        className="hr-success-logo"
                    />

                    <h1 className="hr-success-title">Information Submitted</h1>

                    <p>
                        Your information has been successfully submitted to the
                        CyberAries HR team.
                    </p>

                    <p className="hr-success-note">
                        Please keep this page open only if you need to review your
                        submission confirmation.
                    </p>
                </section>
            </main>
        );
    }

    return (
        <main className="hr-page">
            <section className="hr-header">
                <div className="hr-brand-container">
                    <span className="hr-brand-text">
                        <span className="brand-cyber">Cyber</span>
                        <span className="brand-aries">Aries</span>
                        <span className="brand-divider">•</span>
                        <span className="brand-hr">HR</span>
                    </span>
                </div>

                <h1>Employee & Intern Onboarding</h1>

                <p>
                    Please provide the following information accurately. This form is
                    intended for official onboarding and HR records.
                </p>

                <div className="hr-record-switch">
                    <label className={form.recordType === "Intern" ? "active" : ""}>
                        <input
                            type="radio"
                            name="recordType"
                            value="Intern"
                            checked={form.recordType === "Intern"}
                            onChange={handleChange}
                        />
                        Intern
                    </label>

                    <label className={form.recordType === "Employee" ? "active" : ""}>
                        <input
                            type="radio"
                            name="recordType"
                            value="Employee"
                            checked={form.recordType === "Employee"}
                            onChange={handleChange}
                        />
                        Employee
                    </label>
                </div>
            </section>

            <form className="hr-form" onSubmit={handleSubmit}>
                <FormSection
                    number="01"
                    title="Personal Information"
                    description="Basic personal and contact information."
                >
                    <div className="hr-grid">
                        <Input
                            label="Full Name"
                            name="fullName"
                            value={form.fullName}
                            onChange={handleChange}
                            placeholder="e.g. Rahul Sharma"
                            pattern="^[a-zA-Z\s.\-']{2,}$"
                            maxLength={100}
                            title="Full Name must contain only alphabets and spaces (no numbers)"
                            required
                        />

                        <Input
                            label="Preferred Name"
                            name="preferredName"
                            value={form.preferredName}
                            onChange={handleChange}
                            placeholder="e.g. Rahul"
                            pattern="^[a-zA-Z\s.\-']{2,}$"
                            maxLength={100}
                            title="Preferred Name must contain only alphabets and spaces (no numbers)"
                            required
                        />

                        <Input
                            label="Personal Email Address"
                            type="email"
                            name="personalEmail"
                            value={form.personalEmail}
                            onChange={handleChange}
                            placeholder="e.g. rahul@example.com"
                            maxLength={100}
                            required
                        />

                        <Input
                            label="Phone Number"
                            name="phoneNumber"
                            value={form.phoneNumber}
                            onChange={handleChange}
                            placeholder="10-digit mobile number"
                            pattern="[0-9]{10}"
                            minLength={10}
                            maxLength={10}
                            inputMode="numeric"
                            title="Phone number must be exactly 10 digits"
                            required
                        />

                        <Input
                            label="Date of Birth"
                            type="date"
                            name="dateOfBirth"
                            value={form.dateOfBirth}
                            onChange={handleChange}
                            max="2012-12-31"
                            min="1950-01-01"
                            required
                        />

                        <Select
                            label="Gender"
                            name="gender"
                            value={form.gender}
                            onChange={handleChange}
                            options={["Male", "Female", "Other", "Prefer not to say"]}
                            required
                        />

                        <Input
                            label="City"
                            name="city"
                            value={form.city}
                            onChange={handleChange}
                            placeholder="e.g. Mumbai"
                            pattern="^[a-zA-Z\s.\-']{2,}$"
                            maxLength={50}
                            title="City must contain only letters and spaces"
                            required
                        />

                        <Input
                            label="State"
                            name="state"
                            value={form.state}
                            onChange={handleChange}
                            placeholder="e.g. Maharashtra"
                            pattern="^[a-zA-Z\s.\-']{2,}$"
                            maxLength={50}
                            title="State must contain only letters and spaces"
                            required
                        />

                        <Input
                            label="PIN Code"
                            name="pinCode"
                            value={form.pinCode}
                            onChange={handleChange}
                            placeholder="6-digit PIN code"
                            pattern="[0-9]{6}"
                            minLength={6}
                            maxLength={6}
                            inputMode="numeric"
                            title="PIN Code must be exactly 6 digits"
                            required
                        />
                    </div>

                    <Textarea
                        label="Current Residential Address"
                        name="currentResidentialAddress"
                        value={form.currentResidentialAddress}
                        onChange={handleChange}
                        placeholder="Enter your current residential address"
                        maxLength={500}
                        required
                    />

                    <Textarea
                        label="Permanent Address"
                        name="permanentAddress"
                        value={form.permanentAddress}
                        onChange={handleChange}
                        placeholder="Enter your permanent address"
                        maxLength={500}
                        required
                    />

                    <h3 className="hr-subheading">Emergency Contact</h3>

                    <div className="hr-grid">
                        <Input
                            label="Emergency Contact Name"
                            name="emergencyContactName"
                            value={form.emergencyContactName}
                            onChange={handleChange}
                            placeholder="e.g. Priya Sharma"
                            pattern="^[a-zA-Z\s.\-']{2,}$"
                            maxLength={100}
                            title="Emergency Contact Name must contain only alphabets and spaces (no numbers)"
                            required
                        />

                        <Input
                            label="Relationship"
                            name="emergencyContactRelationship"
                            value={form.emergencyContactRelationship}
                            onChange={handleChange}
                            placeholder="e.g. Parent / Spouse / Sibling"
                            maxLength={50}
                            required
                        />

                        <Input
                            label="Emergency Contact Phone"
                            name="emergencyContactPhone"
                            value={form.emergencyContactPhone}
                            onChange={handleChange}
                            placeholder="10-digit mobile number"
                            pattern="[0-9]{10}"
                            minLength={10}
                            maxLength={10}
                            inputMode="numeric"
                            title="Emergency contact phone must be exactly 10 digits"
                            required
                        />
                    </div>
                </FormSection>

                <FormSection
                    number="02"
                    title="Education"
                    description="Academic background and qualifications."
                >
                    <div className="hr-grid">
                        <Input
                            label="Highest Qualification"
                            name="highestQualification"
                            value={form.highestQualification}
                            onChange={handleChange}
                            placeholder="e.g. Bachelor of Technology (B.Tech)"
                            maxLength={100}
                            required
                        />

                        <Input
                            label="College / University / Institution"
                            name="collegeUniversity"
                            value={form.collegeUniversity}
                            onChange={handleChange}
                            placeholder="e.g. Mumbai University"
                            maxLength={150}
                            required
                        />

                        <Input
                            label="Degree / Course"
                            name="degreeCourse"
                            value={form.degreeCourse}
                            onChange={handleChange}
                            placeholder="e.g. Computer Science Engineering"
                            maxLength={100}
                            required
                        />

                        <Input
                            label="Branch / Specialization"
                            name="branchSpecialization"
                            value={form.branchSpecialization}
                            onChange={handleChange}
                            placeholder="e.g. Information Technology / Cybersecurity"
                            maxLength={100}
                            required
                        />

                        <Input
                            label="Current Year / Semester"
                            name="currentYearSemester"
                            value={form.currentYearSemester}
                            onChange={handleChange}
                            placeholder="e.g. Final Year / 8th Semester"
                            maxLength={50}
                            required
                        />

                        <Input
                            label="Graduation Year"
                            name="graduationYear"
                            value={form.graduationYear}
                            onChange={handleChange}
                            placeholder="YYYY (e.g. 2024)"
                            pattern="^(19[7-9][0-9]|20[0-9]{2})$"
                            minLength={4}
                            maxLength={4}
                            inputMode="numeric"
                            title="Graduation Year must be a 4-digit valid year"
                            required
                        />

                        <Input
                            label="CGPA / Percentage"
                            name="cgpaPercentage"
                            value={form.cgpaPercentage}
                            onChange={handleChange}
                            placeholder="e.g. 8.5 CGPA or 85%"
                            maxLength={20}
                            required
                        />
                    </div>

                    <Textarea
                        label="Professional Certifications (if applicable)"
                        name="professionalCertifications"
                        value={form.professionalCertifications}
                        onChange={handleChange}
                        placeholder="e.g. CEH, CompTIA Security+, AWS Certified (if any)"
                        maxLength={500}
                    />
                </FormSection>

                <FormSection
                    number="03"
                    title="Previous Experience"
                    description="Tell us about previous employment or internship experience."
                >
                    <div className="hr-experience-choice">
                        <span>Previous work / internship experience?</span>

                        <label>
                            <input
                                type="radio"
                                name="previousExperience"
                                value="No"
                                checked={form.previousExperience === "No"}
                                onChange={handleChange}
                            />
                            No
                        </label>

                        <label>
                            <input
                                type="radio"
                                name="previousExperience"
                                value="Yes"
                                checked={form.previousExperience === "Yes"}
                                onChange={handleChange}
                            />
                            Yes
                        </label>
                    </div>

                    {form.previousExperience === "Yes" && (
                        <div className="hr-grid">
                            <Input
                                label="Previous Organization / Company"
                                name="previousOrganization"
                                value={form.previousOrganization}
                                onChange={handleChange}
                                placeholder="e.g. ABC Technologies Pvt Ltd"
                                maxLength={100}
                                required={form.previousExperience === "Yes"}
                            />

                            <Input
                                label="Previous Designation / Role"
                                name="previousDesignation"
                                value={form.previousDesignation}
                                onChange={handleChange}
                                placeholder="e.g. Security Analyst Intern"
                                maxLength={100}
                                required={form.previousExperience === "Yes"}
                            />

                            <Input
                                label="Start Date"
                                type="date"
                                name="previousStartDate"
                                value={form.previousStartDate}
                                onChange={handleChange}
                                required={form.previousExperience === "Yes"}
                            />

                            <Input
                                label="End Date"
                                type="date"
                                name="previousEndDate"
                                value={form.previousEndDate}
                                onChange={handleChange}
                                required={form.previousExperience === "Yes"}
                            />

                            <Input
                                label="Duration"
                                name="previousDuration"
                                value={form.previousDuration}
                                onChange={handleChange}
                                placeholder="e.g. 6 Months"
                                maxLength={50}
                                required={form.previousExperience === "Yes"}
                            />
                        </div>
                    )}

                    {form.previousExperience === "Yes" && (
                        <Textarea
                            label="Key Responsibilities"
                            name="keyResponsibilities"
                            value={form.keyResponsibilities}
                            onChange={handleChange}
                            placeholder="Brief summary of duties and responsibilities"
                            maxLength={500}
                            required={form.previousExperience === "Yes"}
                        />
                    )}
                </FormSection>

                <FormSection
                    number="04"
                    title="Bank & Statutory Information"
                    description="Information required for applicable HR and statutory processing."
                >
                    <div className="hr-grid">
                        <Input
                            label="Bank Account Holder Name"
                            name="bankAccountHolderName"
                            value={form.bankAccountHolderName}
                            onChange={handleChange}
                            placeholder="Name as per bank passbook / records"
                            pattern="^[a-zA-Z\s.\-']{2,}$"
                            maxLength={100}
                            title="Account holder name must contain only alphabets and spaces"
                            required
                        />

                        <Input
                            label="Bank Name"
                            name="bankName"
                            value={form.bankName}
                            onChange={handleChange}
                            placeholder="e.g. HDFC Bank / State Bank of India"
                            maxLength={100}
                            required
                        />

                        <Input
                            label="Account Number"
                            name="accountNumber"
                            value={form.accountNumber}
                            onChange={handleChange}
                            placeholder="9 to 18 digit account number"
                            pattern="[0-9]{9,18}"
                            minLength={9}
                            maxLength={18}
                            inputMode="numeric"
                            title="Bank Account Number must be between 9 and 18 digits (numbers only)"
                            required
                        />

                        <Input
                            label="IFSC"
                            name="ifsc"
                            value={form.ifsc}
                            onChange={handleChange}
                            placeholder="e.g. HDFC0001234"
                            pattern="^[A-Z]{4}0[A-Z0-9]{6}$"
                            minLength={11}
                            maxLength={11}
                            title="IFSC Code must be 11 characters (e.g. HDFC0001234)"
                            required
                        />

                        <Input
                            label="PAN"
                            name="pan"
                            value={form.pan}
                            onChange={handleChange}
                            placeholder="e.g. ABCDE1234F"
                            pattern="^[A-Z]{5}[0-9]{4}[A-Z]{1}$"
                            minLength={10}
                            maxLength={10}
                            title="PAN must be in standard 10-character format (e.g. ABCDE1234F)"
                            required
                        />

                        <Input
                            label="UAN / PF Details (if applicable)"
                            name="uanPfDetails"
                            value={form.uanPfDetails}
                            onChange={handleChange}
                            placeholder="12-digit UAN / PF number"
                            maxLength={25}
                        />

                        <Input
                            label="ESIC Details (if applicable)"
                            name="esicDetails"
                            value={form.esicDetails}
                            onChange={handleChange}
                            placeholder="ESIC IP number"
                            maxLength={25}
                        />
                    </div>
                </FormSection>

                <FormSection
                    number="05"
                    title="Documents"
                    description="Upload the required onboarding documents."
                >
                    <div className="hr-documents">
                        {documentFields.map(([name, label, required]) => (
                            <div className="hr-file" key={name}>
                                <div className="hr-file-header">
                                    <strong>
                                        {label} {required && <span className="hr-required-asterisk">*</span>}
                                    </strong>
                                </div>

                                {(!files[name] || files[name].length === 0 || name === "otherDocuments") && (
                                    <input
                                        type="file"
                                        multiple={name === "otherDocuments"}
                                        onChange={(e) => handleFileChange(e, name)}
                                        required={required && (!files[name] || files[name].length === 0)}
                                        className="hr-file-input"
                                    />
                                )}

                                {files[name] && files[name].length > 0 && (
                                    <div className="hr-uploaded-files">
                                        {files[name].map((file, idx) => (
                                            <div key={idx} className="hr-uploaded-file-item">
                                                <span className="hr-file-name" title={file.name}>
                                                    {file.name}
                                                </span>
                                                <div className="hr-file-actions">
                                                    <button
                                                        type="button"
                                                        className="hr-file-btn view"
                                                        onClick={() => handleViewFile(file)}
                                                    >
                                                        View
                                                    </button>
                                                    <button
                                                        type="button"
                                                        className="hr-file-btn delete"
                                                        onClick={() => handleRemoveFile(name, idx)}
                                                    >
                                                        Remove
                                                    </button>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                        ))}
                    </div>
                </FormSection>

                <FormSection
                    number="06"
                    title="Declarations & Consent"
                    description="Please read and acknowledge each declaration before submitting."
                >
                    <div className="hr-consents">
                        <Checkbox
                            name="declarationAccuracy"
                            checked={form.declarationAccuracy}
                            onChange={handleChange}
                            label="I declare that the information provided in this form is accurate and complete."
                        />

                        <Checkbox
                            name="hrProcessingConsent"
                            checked={form.hrProcessingConsent}
                            onChange={handleChange}
                            label="I acknowledge and consent to the processing of my personal information for HR and onboarding purposes."
                        />

                        <Checkbox
                            name="documentAuthenticity"
                            checked={form.documentAuthenticity}
                            onChange={handleChange}
                            label="I declare that the documents submitted by me are authentic and belong to me."
                        />

                        <Checkbox
                            name="policyAcknowledgement"
                            checked={form.policyAcknowledgement}
                            onChange={handleChange}
                            label="I acknowledge the applicable CyberAries company policies."
                        />

                        <Checkbox
                            name="confirmation"
                            checked={form.confirmation}
                            onChange={handleChange}
                            label="I confirm that I have reviewed the information provided and wish to submit this form."
                        />
                    </div>
                </FormSection>

                <div className="hr-code-of-conduct-section">
                    <h3 className="hr-subheading">Code of Conduct</h3>
                    <p className="hr-code-of-conduct-text">
                        Please read the{" "}
                        <a
                            href="https://drive.google.com/file/d/1uqSIDaEVsPHJi3ZSW6H-jmXOJtACSSFD/view?usp=drive_link"
                            target="_blank"
                            rel="noopener noreferrer"
                            className="hr-code-of-conduct-link"
                        >
                            Code of Conduct
                        </a>{" "}
                        before submitting this form.
                    </p>
                    <label className="hr-checkbox">
                        <input
                            type="checkbox"
                            name="codeOfConductAccepted"
                            checked={form.codeOfConductAccepted}
                            onChange={handleChange}
                            required
                        />
                        <span>I have read and understood the CyberAries Code of Conduct and agree to comply with it.</span>
                    </label>
                </div>

                {error && <div className="hr-error">{error}</div>}

                <div className="hr-submit">
                    <button type="submit" disabled={submitting}>
                        {submitting ? "Submitting..." : "Submit Information"}
                    </button>

                    <p>
                        Your information will be handled through the company's HR
                        onboarding system.
                    </p>
                </div>
            </form>

            {previewFile && (
                <div className="hr-preview-modal-overlay" onClick={handleClosePreview}>
                    <div className="hr-preview-modal-content" onClick={(e) => e.stopPropagation()}>
                        <div className="hr-preview-modal-header">
                            <span className="hr-preview-modal-title">{previewFile.name}</span>
                            <button className="hr-preview-modal-close" onClick={handleClosePreview}>
                                &times;
                            </button>
                        </div>
                        <div className="hr-preview-modal-body">
                            {previewFile.type === "image" && (
                                <img src={previewFile.url} alt={previewFile.name} className="hr-preview-image" />
                            )}
                            {previewFile.type === "pdf" && (
                                <iframe src={previewFile.url} title="PDF Preview" className="hr-preview-pdf" />
                            )}
                            {previewFile.type === "other" && (
                                <div className="hr-preview-fallback">
                                    <p>Preview not available for this file type.</p>
                                    <a href={previewFile.url} download={previewFile.name} className="hr-preview-download-btn">
                                        Download File
                                    </a>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            )}
        </main>
    );
}

function FormSection({ number, title, description, children }) {
    return (
        <section className="hr-section">
            <div className="hr-section-heading">
                <span>{number}</span>

                <div>
                    <h2>{title}</h2>
                    <p>{description}</p>
                </div>
            </div>

            {children}
        </section>
    );
}

function Input({ label, required, ...props }) {
    return (
        <label className="hr-field">
            <span>
                {label} {required && <span className="hr-required-asterisk">*</span>}
            </span>
            <input required={required} {...props} />
        </label>
    );
}

function Textarea({ label, required, ...props }) {
    return (
        <label className="hr-field">
            <span>
                {label} {required && <span className="hr-required-asterisk">*</span>}
            </span>
            <textarea rows="4" required={required} {...props} />
        </label>
    );
}

function Select({ label, options, required, ...props }) {
    return (
        <label className="hr-field">
            <span>
                {label} {required && <span className="hr-required-asterisk">*</span>}
            </span>

            <select required={required} {...props}>
                <option value="">Select</option>

                {options.map((option) => (
                    <option key={option} value={option}>
                        {option}
                    </option>
                ))}
            </select>
        </label>
    );
}

function Checkbox({ name, checked, onChange, label }) {
    return (
        <label className="hr-checkbox">
            <input
                type="checkbox"
                name={name}
                checked={checked}
                onChange={onChange}
                required
            />

            <span>{label}</span>
        </label>
    );
}

export default HROnboarding;